import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const SkillCircle: React.FC<{ name: string; level: number; compact?: boolean }> = ({
    name,
    level,
    compact,
}) => {
    const size = compact ? 36 : 44;
    const r = compact ? 14 : 17;
    const c = 2 * Math.PI * r;
    return (
        <div className="flex flex-col items-center min-w-0">
            <div className="relative flex items-center justify-center mb-1" style={{ width: size, height: size }}>
                <svg width={size} height={size} className="transform -rotate-90">
                    <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.15)" strokeWidth="3" fill="transparent" />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        stroke="white"
                        strokeWidth="3"
                        fill="transparent"
                        strokeDasharray={c}
                        strokeDashoffset={c * (1 - level / 100)}
                    />
                </svg>
                <span className="absolute text-[7px] font-bold">{Math.round(level)}%</span>
            </div>
            <span className="text-[8px] font-bold uppercase tracking-wide text-center leading-tight break-words w-full px-0.5">
                {name}
            </span>
        </div>
    );
};

const ModernBlueTemplate: React.FC<TemplateProps> = ({
    data,
    isContinuationPage = false,
    showPlaceholders = false,
    pageIndex = 0,
    fullData,
}) => {
    const primaryColor = data.style?.primaryColor || '#0077B6';
    const {
        fontFamily = 'font-sans',
        textColor = '#0f172a',
        backgroundColor = '#ffffff',
        sectionBackgroundColor = primaryColor,
        imageBorderColor = 'rgba(255,255,255,0.5)',
    } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+01234 567 890' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'email@exemple.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Adresse' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Décrivez brièvement votre parcours professionnel et vos objectifs.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders
        ? 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1976&auto=format&fit=crop'
        : '');

    const skillsList =
        data.skills && data.skills.length > 0
            ? data.skills
            : showPlaceholders
                ? [
                      { name: 'Design', level: 95 },
                      { name: 'SEO', level: 90 },
                      { name: 'Analyse', level: 85 },
                  ]
                : [];

    const interestsList =
        data.interests && data.interests.length > 0
            ? data.interests
            : showPlaceholders
                ? [{ name: 'Sport' }, { name: 'Lecture' }, { name: 'Voyage' }, { name: 'Musique' }, { name: 'Cinéma' }, { name: 'Cuisine' }]
                : [];

    const renderSkillsGrid = (inSidebar: boolean) => (
        <div className={`grid grid-cols-3 gap-x-1 gap-y-3 ${inSidebar ? '' : 'gap-4'}`}>
            {skillsList.map((skill, i) => (
                <div
                    key={skill.id || skill.name || i}
                    data-cv-block
                    data-cv-item-id={skill.id || undefined}
                >
                    <SkillCircle
                        name={skill.name}
                        level={skill.level || 50}
                        compact={inSidebar}
                    />
                </div>
            ))}
        </div>
    );

    return (
        <div
            className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex overflow-hidden ${fontFamily} relative`}
            style={{ backgroundColor, color: textColor }}
        >
            <div className="absolute top-[-50px] left-[-50px] w-64 h-64 rounded-full opacity-10 z-0" style={{ backgroundColor: primaryColor }} />
            <div className="absolute bottom-10 right-[-30px] w-32 h-32 rounded-full opacity-10 z-0" style={{ backgroundColor: primaryColor }} />

            <div className="w-1/3 text-white p-5 flex flex-col z-10 overflow-hidden" style={{ backgroundColor: sectionBackgroundColor }}>
                {isContinuationPage ? (
                    <div className="mt-4">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <>
                        {(photoSrc || showPlaceholders) && (
                            <div className="mb-6 flex flex-col items-center shrink-0">
                                <div className="w-24 h-24 rounded-full border-4 overflow-hidden mb-3 shadow-xl bg-white/10" style={{ borderColor: imageBorderColor }}>
                                    {photoSrc ? (
                                        <img src={photoSrc} alt="Profil" className="w-full h-full object-cover" />
                                    ) : null}
                                </div>
                                <div
                                    className="px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest shadow-lg"
                                    style={{ backgroundColor: primaryColor, filter: 'brightness(1.1)' }}
                                >
                                    {CV_LABELS.publicProfile}
                                </div>
                            </div>
                        )}

                        {(phone || email || address || showPlaceholders) && (
                            <section className="mb-5 shrink-0">
                                <h2 className="text-xs font-black uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                                    {CV_LABELS.contact} <div className="h-px flex-1 bg-white/20" />
                                </h2>
                                <div className="cv-fs-contact space-y-2.5">
                                    {phone && (
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 bg-white/10 rounded-md shrink-0"><Phone className="w-3 h-3" /></div>
                                            <span className="text-[10px] font-medium break-all">{phone}</span>
                                        </div>
                                    )}
                                    {email && (
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 bg-white/10 rounded-md shrink-0"><Mail className="w-3 h-3" /></div>
                                            <span className="text-[10px] font-medium break-all">{email}</span>
                                        </div>
                                    )}
                                    {address && (
                                        <div className="flex items-center gap-2">
                                            <div className="p-1 bg-white/10 rounded-md shrink-0"><MapPin className="w-3 h-3" /></div>
                                            <span className="text-[10px] font-medium break-words">{address}</span>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {summary && (
                            <section className="mb-5 shrink-0">
                                <h2 className="text-xs font-black uppercase tracking-[0.15em] mb-2 flex items-center gap-2">
                                    {CV_LABELS.about} <div className="h-px flex-1 bg-white/20" />
                                </h2>
                                <p className="cv-fs-summary text-[10px] leading-relaxed font-medium break-words line-clamp-6" style={{ color: 'rgba(255,255,255,0.9)' }}>
                                    {summary}
                                </p>
                            </section>
                        )}

                        {skillsList.length > 0 && (
                            <section className="min-h-0 overflow-hidden">
                                <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor="#ffffff" variant="plain" />
                                <div className="cv-fs-skills">
                                {renderSkillsGrid(true)}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>

            <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-8 flex flex-col z-10">
                {isContinuationPage ? (
                    <div className="mb-4 pb-2 border-b border-slate-200 shrink-0">
                        <p className="text-sm font-bold uppercase tracking-widest text-slate-400">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <header className="mb-6 shrink-0">
                        <h1 className="cv-fs-name text-4xl font-black tracking-tighter uppercase leading-none mb-2 break-words" style={{ color: primaryColor }}>
                            {data.personalInfo.firstName || (showPlaceholders ? 'PRÉNOM' : '')}{' '}
                            <span className="text-slate-900">{data.personalInfo.lastName || (showPlaceholders ? 'NOM' : '')}</span>
                        </h1>
                        <p className="cv-fs-profession text-sm text-slate-500 font-bold uppercase tracking-[0.2em]">
                            {data.personalInfo.profession || (showPlaceholders ? 'VOTRE MÉTIER' : '')}
                        </p>
                    </header>
                )}

                {/* Compétences en page de suite (si elles ont débordé) */}
                {isContinuationPage && skillsList.length > 0 && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-skills grid grid-cols-3 gap-4">
                            {skillsList.map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[8px] font-bold shrink-0" style={{ borderColor: primaryColor, color: primaryColor }}>
                                        {Math.round(skill.level || 50)}%
                                    </div>
                                    <span className="text-xs font-bold break-words" style={{ color: textColor }}>{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {(data.experience.length > 0 || showPlaceholders) && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-experience space-y-5">
                            {data.experience.length > 0
                                ? data.experience.map((exp, i) => (
                                      <div
                                          key={exp.id || i}
                                          data-cv-block
                                          data-cv-item-id={exp.id || undefined}
                                      >
                                          <p className="text-[10px] font-bold text-slate-400 mb-0.5">
                                              {exp.startDate} - {frPresent(exp.isCurrent, exp.endDate)}
                                          </p>
                                          <h3 className="text-sm font-black uppercase break-words" style={{ color: textColor }}>{exp.position}</h3>
                                          <p className="text-xs font-bold mb-1 break-words" style={{ color: primaryColor }}>{exp.company}</p>
                                          {exp.description && (
                                              <p className="text-[10px] leading-relaxed break-words" style={{ color: textColor, opacity: 0.8 }}>{exp.description}</p>
                                          )}
                                      </div>
                                  ))
                                : showPlaceholders ? (
                                      <div>
                                          <p className="text-[10px] font-bold text-slate-400 mb-0.5">2020 - {CV_LABELS.present}</p>
                                          <h3 className="text-sm font-black uppercase" style={{ color: textColor }}>Poste occupé</h3>
                                          <p className="text-xs font-bold mb-1" style={{ color: primaryColor }}>Nom de l&apos;entreprise</p>
                                      </div>
                                  ) : null}
                        </div>
                    </section>
                )}

                {(data.education.length > 0 || showPlaceholders) && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-education space-y-4">
                            {data.education.length > 0
                                ? data.education.map((edu, i) => (
                                      <div
                                          key={edu.id || i}
                                          data-cv-block
                                          data-cv-item-id={edu.id || undefined}
                                      >
                                          <p className="text-[10px] font-bold text-slate-400">
                                              {edu.startDate} - {frPresent(edu.isCurrent, edu.endDate)}
                                          </p>
                                          <h3 className="text-sm font-black uppercase break-words" style={{ color: textColor }}>{edu.degree}</h3>
                                          <p className="text-xs font-bold mb-1 break-words" style={{ color: primaryColor }}>{edu.institution}</p>
                                          {edu.description && (
                                              <p className="text-[10px] leading-relaxed break-words" style={{ color: textColor, opacity: 0.8 }}>{edu.description}</p>
                                          )}
                                      </div>
                                  ))
                                : showPlaceholders ? (
                                      <div>
                                          <p className="text-[10px] font-bold text-slate-400">2018 - 2022</p>
                                          <h3 className="text-sm font-black uppercase" style={{ color: textColor }}>Diplôme</h3>
                                          <p className="text-xs font-bold" style={{ color: primaryColor }}>Établissement</p>
                                      </div>
                                  ) : null}
                        </div>
                    </section>
                )}

                {interestsList.length > 0 && (
                    <section>
                        <h2 className="text-base font-black uppercase tracking-[0.1em] mb-4 flex items-center gap-3" style={{ color: primaryColor }}>
                            {CV_LABELS.interests} <div className="h-1 flex-1 rounded-full" style={{ backgroundColor: `${primaryColor}20` }} />
                        </h2>
                        <div className="cv-fs-interests grid grid-cols-3 gap-x-3 gap-y-2">
                            {interestsList.map((interest, i) => (
                                <div
                                    key={interest.id || interest.name || i}
                                    className="flex items-center gap-1.5 min-w-0"
                                    data-cv-block
                                    data-cv-item-id={interest.id || undefined}
                                >
                                    <div className="w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0" style={{ borderColor: `${primaryColor}50` }}>
                                        <div className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: primaryColor }} />
                                    </div>
                                    <span className="text-[10px] font-bold truncate" style={{ color: textColor }}>{interest.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>

            <div className="absolute bottom-[-100px] left-[50px] w-64 h-64 rounded-full opacity-10 z-0 pointer-events-none" style={{ backgroundColor: primaryColor }} />
        </div>
    );
};

export default ModernBlueTemplate;
